import { AdminUser, AdminUserBase } from "@preveil-api";
import { AdminUIActionTypes, AdminErrorMessages, removeNonAsciiChars } from "src/common";
import Papa from "papaparse";

export enum FileUploadTypes {
  CSV = ".csv",
  CERTIFICATE = ".crt, .pem, .cer"
}

// These are the user_id/user email column names for each one of the csv files we provide in Admin.
export enum UserIdColumn {
  DOMAIN_OR_EMAIL = "Domain or User Address",
  USER_ADDRESS = "User Address",
  EMAIL_ADDRESS = "Email Address"
}

// Description: Parses a CSV file and processes its data using the provided handleAction callback.
function parseCSVFile(data: File, handleAction: (params: any) => void) {
  Papa.parse(data, {
    header: true,
    skipEmptyLines: true,
    delimiter: ",",
    transformHeader: (header) => header.trim(),
    transform: (value: string, column: string | number) => {
      let _value;
      if (column === UserIdColumn.DOMAIN_OR_EMAIL || column === UserIdColumn.USER_ADDRESS || column === UserIdColumn.EMAIL_ADDRESS) {
        _value = value.trim().toLowerCase();
      } else {
        _value = value.trim();
      }
      return removeNonAsciiChars(_value);
    },
    complete: function (results: Papa.ParseResult<any>) {
      return handleAction({ actionType: AdminUIActionTypes.CsvData, params: results });
    }
  });
}

// Description: Converts a DER-encoded binary certificate (ArrayBuffer) to a PEM-formatted string.
function derToPem(buffer: ArrayBuffer): string {
  const binary_array = new Uint8Array(buffer);
  let binary_string = "";
  binary_array.forEach((byte) => {
    binary_string += String.fromCharCode(byte);
  });
  const base64_string = btoa(binary_string);
  const lines = base64_string.match(/.{1,64}/g) ?? [];
  return `-----BEGIN CERTIFICATE-----\n${lines.join("\n")}\n-----END CERTIFICATE-----`;
}

// Description: Reads and extracts a certificate from a file, then processes it using the provided handleAction callback.
// NOTE: Reads the file as ArrayBuffer. TextDecoder is used to check for PEM markers; if absent the buffer is treated as DER binary and converted to PEM.
function parseCertificateFile(data: File, handleAction: (params: any) => void) {
  const reader = new FileReader();
  reader.onload = () => {
    const buffer = reader.result as ArrayBuffer;
    const text = new TextDecoder().decode(buffer);
    const start_index = text.indexOf("-----BEGIN CERTIFICATE-----");
    const end_index = text.indexOf("-----END CERTIFICATE-----");
    if (start_index !== -1 && end_index !== -1 && end_index > start_index) {
      const extracted_certificate = text.substring(start_index, end_index + "-----END CERTIFICATE-----".length);
      handleAction({ actionType: AdminUIActionTypes.DeviceCertificateData, params: { cert: extracted_certificate } });
    } else if (start_index === -1 && end_index === -1 && new Uint8Array(buffer)[0] === 0x30) {
      // NOTE: PEM markers not found so treat as DER-encoded binary (common for .cer files exported from Windows/macOS)
      handleAction({ actionType: AdminUIActionTypes.DeviceCertificateData, params: { cert: derToPem(buffer) } });
    } else {
      handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_invalid_certificate_format } });
    }
  };
  reader.onerror = () => {
    handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_invalid_certificate_format } });
  };
  reader.readAsArrayBuffer(data);
}

// Description: Determines the file type and delegates parsing to the appropriate function, then processes the result using handleAction.
export function parseFile(data: File, type: FileUploadTypes, handleAction: (params: any) => void) {
  switch (type) {
    case FileUploadTypes.CSV:
      parseCSVFile(data, handleAction);
      break;
    case FileUploadTypes.CERTIFICATE:
      parseCertificateFile(data, handleAction);
      break;
  }
}

// Description: Returns the appropriate error message for the given file upload type.
export function getError(type: FileUploadTypes) {
  switch (type) {
    case FileUploadTypes.CSV:
      return AdminErrorMessages.error_csv_wrong_file_type;
    case FileUploadTypes.CERTIFICATE:
      return AdminErrorMessages.error_invalid_certificate_extension;
  }
}

// Description: Tests if the file name matches the expected extension(s) for the given file upload type.
export function testExtension(name: string, type: FileUploadTypes) {
  switch (type) {
    case FileUploadTypes.CSV:
      return /^.*\.(csv|CSV)$/.test(name);
    case FileUploadTypes.CERTIFICATE:
      return /^.*\.(crt|pem|cer)$/.test(name);
  }
}

// Description: Returns the drag-and-drop title for the given file upload type.
export function getTitle(type: FileUploadTypes) {
  switch (type) {
    case FileUploadTypes.CSV:
      return "Drag .CSV file here";
    case FileUploadTypes.CERTIFICATE:
      return "Drag .CRT/.PEM/.CER file here";
  }
}

// Description: Maps an AdminUserBase to an AdminUser.
export function mapUserToAdminUser(user: AdminUserBase): AdminUser {
  return {
    account_type: user.account_type,
    account_version: user.account_version,
    department: user.department,
    devices: [],
    display_name: user.display_name,
    pending_account_type: user.pending_account_type ?? undefined,
    recovery_group_id: user.recovery_group_id,
    role: user.entity_role,
    status: user.claimed_at !== null ? "active" : "pending",
    user_id: user.user_id,
  };
}
